import { useEffect, useState } from 'react';
import { ALWAYS_LOADING, AsyncResolution, AsyncResult } from '../../types/AsyncResult';

/** A serializable cache of the outcomes of async actions.
 *
 * The backing storage can be passed from server to browser and then used to recreate the cache. This allows asynchronous actions
 * which already completed on the server or on a previous page to be synchronously available when rendering.
 *
 * Every action must have a unique string key.
 */
export class AsyncCache<T> {
  constructor(private resolutionStorage: Record<string, AsyncResolution<T>>) {}

  private entries = new Map<string, AsyncCacheEntry<T>>();

  public run = (key: string, action: () => PromiseLike<T>): AsyncCacheEntry<T> => {
    const storedEntry = this.entries.get(key);
    if (storedEntry) return storedEntry;

    const storedResolution = this.resolutionStorage[key];

    if (storedResolution) {
      const entry = AsyncCacheEntry.fromResolution(storedResolution);
      this.entries.set(key, entry);
      return entry;
    }

    const entry = AsyncCacheEntry.fromPromise(action());
    this.entries.set(key, entry);

    void entry.promise.then(resolution => {
      this.resolutionStorage[key] = resolution;
    });

    return entry;
  };
}

/** An entry which makes an async outcome available both synchronously (if possible) and asynchronously. */
export class AsyncCacheEntry<T> {
  private constructor(
    public promise: PromiseLike<AsyncResolution<T>>,
    public resolution?: AsyncResolution<T>,
  ) {}

  static fromResolution = <T>(resolution: AsyncResolution<T>): AsyncCacheEntry<T> =>
    new AsyncCacheEntry(Promise.resolve(resolution), resolution);

  static fromPromise = <T>(innerPromise: PromiseLike<T>): AsyncCacheEntry<T> => {
    const promise: PromiseLike<AsyncResolution<T>> = innerPromise.then(
      data => ({ data }),
      (error: unknown) => ({ error }),
    );

    const entry = new AsyncCacheEntry<T>(promise);

    void promise.then(resolution => {
      entry.resolution = resolution;
    });

    return entry;
  };
}

/** Bridges a cache entry to React.
 * @returns an AsyncResult representing the state of the action.
 * If the action has already resolved, its value will be immediately available.
 */
export const useAsyncCacheEntry = <T>(entry: AsyncCacheEntry<T>): AsyncResult<T> => {
  const [result, setResult] = useState<AsyncResult<T>>(() =>
    entry.resolution ? { loading: false, ...entry.resolution } : ALWAYS_LOADING,
  );

  useEffect(() => {
    if (entry.resolution) return;
    let cancelled = false;

    void entry.promise.then(resolution => {
      if (!cancelled) setResult({ loading: false, ...resolution });
    });

    return () => {
      cancelled = true;
    };
  }, [entry]);

  return result;
};
